import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Input } from "@acme/ui/input";
import { Button } from "@acme/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@acme/ui/dropdown-menu";
import { Switch } from "@acme/ui/switch";
import { Badge } from "@acme/ui/badge";
import { Card } from "@acme/ui/card";
import { api } from "~/common/utils/trpc.js";
import { MoreHorizontal } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CwNotificationSettingsDialog } from "~/features/clan-war-notifications/_components/cw-notification-settings-dialog.js";
import { useListViewMode } from "~/common/hooks/use-list-view-mode.js";
import { ViewToggle } from "../_components/-view-toggle.js";
import { ConnectionCard } from "../_components/-connection-card.js";
import type { ConnectionItem } from "../_components/-connection-card.js";
import { ClanEmblem } from "../_components/-clan-emblem.js";

export const Route = createFileRoute("/_authed/my-players/$tag/clans")({
  component: ClansTab,
});

function ClansTab()
{
  const { tag } = Route.useParams();
  const queryClient = useQueryClient();
  const { effectiveView, viewMode, setViewMode, isBelowLg } = useListViewMode();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTagToFetch, setSearchTagToFetch] = useState("");
  const [notificationDialogClanTag, setNotificationDialogClanTag] = useState<string | null>(null);

  const overviewQueryOptions = api.player.getOverviewItemByTag.queryOptions(
    { playerTag: tag },
    { enabled: tag.length > 0 }
  );
  const { data: overview } = useQuery(overviewQueryOptions);

  const connectionsQueryOptions = api.player.getAllConnectionsToClansByTag.queryOptions(
    { playerTag: tag },
    { placeholderData: (previousData) => previousData }
  );
  const { data, isLoading: isConnectionsLoading } = useQuery(connectionsQueryOptions);

  const emptyConnections = { connected: [] as const, connection_requested: [] as const };
  const connections = data ?? emptyConnections;
  const allClans = useMemo(
    () => [...connections.connected, ...connections.connection_requested],
    [connections.connected, connections.connection_requested]
  );

  const clanTagsInConnections = useMemo(() => new Set(allClans.map((c) => c.clanTag)), [allClans]);
  const canRequestSearchedTag = searchTagToFetch.length >= 5 && !clanTagsInConnections.has(searchTagToFetch);

  const requestConnectionMutationOptions = api.shared.connections.requestOrAccept.mutationOptions({
    onSuccess: () =>
    {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({ playerTag: tag }),
      });
    },
  });
  const requestConnection = useMutation(requestConnectionMutationOptions);

  const removeConnectionMutationOptions = api.shared.connections.remove.mutationOptions({
    onSuccess: () =>
    {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({ playerTag: tag }),
      });
    },
  });
  const removeConnection = useMutation(removeConnectionMutationOptions);

  const toggleCwNotificationsMutationOptions = api.player.toggleCwNotifications.mutationOptions({
    onSuccess: () =>
    {
      void queryClient.invalidateQueries({
        queryKey: api.player.getAllConnectionsToClansByTag.queryKey({ playerTag: tag }),
      });
    },
  });
  const toggleCwNotifications = useMutation(toggleCwNotificationsMutationOptions);

  const getConnectionStatusForDialog = (clanTag: string) =>
  {
    const conn = allClans.find((c) => c.clanTag === clanTag);
    return conn?.status ?? "connection_requested";
  };

  const displayTag = tag.startsWith("#") ? tag : `#${tag}`;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="text-xl font-semibold">
          {overview != null ? "Clan connections" : `Clan connections — ${displayTag}`}
        </h2>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} isBelowLg={isBelowLg} />
      </div>

      <div className="space-y-6">
        <section className="space-y-3" aria-labelledby="add-clan-heading">
          <h2 id="add-clan-heading" className="text-lg font-semibold">
            Add clan
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Clan tag (min. 5 characters)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <Button type="button" className="w-full sm:w-auto" onClick={() => setSearchTagToFetch(searchQuery.trim())}>
              Search
            </Button>
          </div>
          <div className="rounded-lg border border-input bg-card p-4">
            {searchTagToFetch.length < 5 ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">1. Enter clan tag</Badge>
                <span className="text-sm text-muted-foreground">Enter at least 5 characters and click Search.</span>
              </div>
            ) : !canRequestSearchedTag ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">Already in your connections</Badge>
                <span className="text-sm text-muted-foreground">This clan is already in your connection list.</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <Badge variant="outline">Clan found</Badge>
                  <span className="truncate font-medium font-mono text-sm">{searchTagToFetch}</span>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="w-full sm:w-auto shrink-0"
                  onClick={() => requestConnection.mutate({ playerTag: tag, clanTag: searchTagToFetch })}
                  disabled={requestConnection.isPending}>
                  Request connection
                </Button>
              </div>
            )}
          </div>
        </section>

        {effectiveView === "table" ? (
          <div className="rounded-md border border-input overflow-x-auto bg-card">
            {isConnectionsLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading connections...</div>
            ) : allClans.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No clan connections yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clan name</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>CW Notifications</TableHead>
                    <TableHead>Connection status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allClans.map((connection) => (
                    <TableRow key={connection.clanTag}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <ClanEmblem badgeUrls={connection.clanBadgeUrls ?? undefined} size="sm" />
                          <span>{connection.clanName || connection.clanTag}</span>
                        </div>
                      </TableCell>
                      <TableCell>{connection.clanLevel ?? "–"}</TableCell>
                      <TableCell>
                        <Switch
                          checked={connection.cwNotificationsActive}
                          onCheckedChange={(enabled) =>
                            toggleCwNotifications.mutate({
                              playerTag: tag,
                              clanTag: connection.clanTag,
                              enabled,
                            })
                          }
                          size="sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={connection.status === "connected" ? "default" : "secondary"}>
                          {connection.status === "connected" ? "Connected" : "Requested"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="border-input">
                              <DropdownMenuItem onClick={() => setNotificationDialogClanTag(connection.clanTag)}>
                                CW notification settings
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => removeConnection.mutate({ playerTag: tag, clanTag: connection.clanTag })}>
                            {connection.status === "connected" ? "Remove" : "Cancel"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        ) : isConnectionsLoading ? (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 20rem), 1fr))",
            }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <div className="p-4 space-y-3">
                  <div className="h-5 w-3/4 rounded bg-muted animate-pulse" />
                  <div className="h-4 w-1/2 rounded bg-muted animate-pulse" />
                  <div className="h-8 w-20 rounded bg-muted animate-pulse" />
                </div>
              </Card>
            ))}
          </div>
        ) : allClans.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">No clan connections yet.</div>
        ) : (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 20rem), 1fr))",
            }}>
            {allClans.map((connection: ConnectionItem) => (
              <ConnectionCard
                key={connection.clanTag}
                connection={connection}
                playerTag={tag}
                onOpenNotificationSettings={setNotificationDialogClanTag}
                onRemoveConnection={(args) => removeConnection.mutate(args)}
                onToggleCwNotifications={(args) => toggleCwNotifications.mutate(args)}
                isRemovePending={removeConnection.isPending}
              />
            ))}
          </div>
        )}
      </div>

      {notificationDialogClanTag && (
        <CwNotificationSettingsDialog
          playerTag={tag}
          clanTag={notificationDialogClanTag}
          connectionStatus={getConnectionStatusForDialog(notificationDialogClanTag)}
          open={Boolean(notificationDialogClanTag)}
          onOpenChange={(open) =>
          {
            if (!open) setNotificationDialogClanTag(null);
          }}
        />
      )}
    </>
  );
}
